import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useLocation } from 'react-router-dom'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import getBrandName from '../../../util/getBrandName'

import Email from './Email'
import Password from './Password'
import Profile from './Profile'
import Address from './Address'
import End from './End'
import withAppStore from '../../../util/withAppStore.jsx'

const STEPS = ['email', 'password', 'profile', 'address', 'end']

const Signup = function Signup (props) {
  const navigate = useNavigate()
  const location = useLocation()

  const [state, setState] = useState({
    step: 'email',
    submitError: '',
    data: {
      email: null,
      password: null,
      profile: null,
      address: null,
    },
  })

  const [module, setModule] = useState('signup')

  const steps = {
    email: Email,
    password: Password,
    profile: Profile,
    address: Address,
    end: End,
  }

  // The signup page is public, so load the brand for the nav bar logo
  useEffect(() => {
    AppDispatcher.dispatch({ action: 'get-brand', name: getBrandName() })
  }, [])

  // Equivalent to constructor logic
  useEffect(() => {
    if (location.pathname.match(/welcome/)) {
      setState(prevState => ({
        ...prevState,
        step: 'profile',
      }))
      setModule('welcome')
    }
  }, [location.pathname])

  const next = useCallback((data, callback) => {
    const nextStep = STEPS.indexOf(state.step) + 1
    if (nextStep) {
      const step = STEPS[nextStep]
      setState(prevState => ({
        ...prevState,
        step,
        data: {
          ...prevState.data,
          ...data,
        },
      }), () => {
        if (callback) {
          callback()
        }
      })
    }
  }, [state.step])

  const previous = useCallback(() => {
    const previousStep = STEPS.indexOf(state.step) - 1
    if (previousStep >= 0) {
      const step = STEPS[previousStep]
      setState(prevState => ({ ...prevState, step }))
    } else {
      navigate('/login')
    }
  }, [state.step, navigate])

  const login = useCallback(() => {
    navigate('/login')
  }, [navigate])

  const createOrUpdateUser = useCallback((currentData) => {
    console.log('createOrUpdateUser module: ', module)
    const { auth, data: { user } } = props
    const data = currentData || state.data

    if (module === 'signup') {
      auth.createUser(data, (error) => {
        if (error) {
          setState(prevState => ({ ...prevState, submitError: error.toString() }))
          return console.error(error)
        }
      })

      const step = STEPS[STEPS.indexOf('end')]
      return setState(prevState => ({ ...prevState, step }))
    }

    const change = {
      onboarding: true,
      ...data.profile,
      ...data.address,
    }

    // Only include password if it has a value
    if (data.password) {
      change.password = data.password
    }

    AppDispatcher.dispatch({
      action: 'update-user',
      userID: user.id,
      change,
    })

    return navigate('/home')
  }, [props, state.data, module])

  const submit = useCallback((formData) => {
    const updatedData = {
      ...state.data,
      ...formData,
    }

    setState(prevState => ({
      ...prevState,
      data: updatedData,
    }))

    createOrUpdateUser(updatedData)
  }, [state.data, createOrUpdateUser])

  const { step, data, submitError } = state
  const { auth: { checkEmail }, data: { user: { onboarding } } } = props
  const Step = steps[step]

  return (
    <>
      <Helmet><title>Signup</title></Helmet>
      <div>
        <Step
          onboarding={onboarding}
          onNext={next}
          onSubmit={submit}
          onPrevious={previous}
          checkEmail={checkEmail}
          submitError={submitError}
          goToLogin={login}
          logout={() => props.auth.logout(navigate)}
          values={data}
        />
      </div>
    </>
  )
}

Signup.propTypes = {
  auth: PropTypes.object,
  pages: PropTypes.object,
  route: PropTypes.object,
}

export default withAppStore(Signup)
